import {
  Button,
  Checkbox,
  CloseButton,
  Group,
  Input,
  LoadingOverlay,
  Modal,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { DatesRangeValue } from '@mantine/dates';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openModal } from '@mantine/modals';
// import { IconRefresh } from '@tabler/icons-react';
import axios, { CancelTokenSource } from 'axios';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import {
  CanAccess,
  DatasetsFilesInfo,
  DataSourcesType,
  delDatasetsFiles,
  getDatasetsFilesList,
  getFilenameWithoutExtension,
  getWebConnectorFiles,
  IntegrationSubType,
  ListFileStatus,
  ListResponse,
  refreshDatasetsFile,
  refreshDatasetsIntegrationFile,
  refreshDatasetsWebsite,
  useDataSourcesModal,
} from '@mygpt/app';

import { SvgSearch, SvgTrash } from '@/components/Common/icon';
import { SvgCalenderGray } from '@/components/Dashboard/icon';
import { EditForm } from '@/components/Datasets/DataSources/form';
import Th from '@/components/Datasets/DataSources/th';
import DateSelector from '@/components/DateSelector';
import SelectedItemRemove from '@/components/Embeddings/selectedItemRemove';
import TableLoading from '@/components/TableLoading';
import { useDatasetStore } from '@/store/dataset';
import { isFileStatusProcess, isFileStatusReady } from '@/utils/embed';

import TableItem from './item';
import classes from './list.module.css';
import { AdvancedTrainModal } from '@/components/Datasets/AdvancedTrainModal';
import { useGPTStore } from '@/store';
import { useSearchParams } from 'react-router-dom';

const InitPageInfo = {
  page: 1,
  pages: 1,
  size: 50,
  total: 0,
};

export const DataSourcesList = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryStatus = searchParams.get('status') as ListFileStatus;
  const { '*': type, datasetId: datasetIdParam, ruleId: ruleIdParam, connectorId: connectorIdParam } = useParams();
  const integrationRuleId = ruleIdParam as string;
  const connectorId = connectorIdParam as string;
  const datasetId = datasetIdParam as string;
  let fileType = type as DataSourcesType;

  // 检查是否为website-pro路径
  const isWebsitePro = location.pathname.includes('/web-connector/');

  // 集成类型的列表从之前的 integration 路由改成了 integration/8633581a-ed41-4a64-a8f3-8008269fa464
  // 所以这里转换下
  if (fileType.includes('integration')) {
    fileType = 'integration';
  }
  // 对于website-pro，将fileType设置为website以保持兼容性
  if (isWebsitePro) {
    fileType = 'website';
  }

  const isFileType = ['file', 'integration'].includes(fileType || '');

  const { counter, setCounter } = useDataSourcesModal();
  const matches = useMediaQuery('(min-width: 640px)');

  const [initLoading, setInitLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [retrainLoading, setRetrainLoading] = useState(false);

  const isInitRef = useRef(true);
  const { isCloning, setCloningTipOpened, setCount } = useDatasetStore();
  const { configInfo } = useGPTStore();
  const supportAdvancedTrain = configInfo?.advanced_parser.some((item) => item.enabled);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [deleteFileIds, setDeleteFileIds] = useState<string[]>([]);
  const listTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const [filesList, setFilesList] = useState<DatasetsFilesInfo[]>([]);

  const [filesListPageInfo, _setFilesListPageInfo] = useState<ListResponse>(InitPageInfo);
  const filesListPageInfoRef = useRef<ListResponse>(InitPageInfo);
  const setFilesListPageInfo = (pageInfo: ListResponse) => {
    filesListPageInfoRef.current = pageInfo;
    _setFilesListPageInfo(pageInfo);
  };

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);

  const [statusValue, setStatusValue] = useState<ListFileStatus | ''>();
  const [learnTypeValue, setLearnTypeValue] = useState<string | ''>('0');
  // 集成类型的子类型
  const [subIntegrationSubType, setSubIntegrationSubType] = useState<IntegrationSubType | ''>('');

  const [rangeDate, setRangeDate] = useState<string[]>([]);
  const handleDateChange = (date: DatesRangeValue | null, text: string) => {
    let dateTime: string[] = [];
    if (date) {
      dateTime = date.map((item) => (item && dayjs(item).format('YYYY-MM-DD HH:mm:ss')) || '');
    }
    setRangeDate(dateTime);
  };

  const [sortBy, setSortBy] = useState<string>('');
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const setSorting = (field: string) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const [editItem, setEditItem] = useState<DatasetsFilesInfo>();
  const [editModalOpened, setEditModalOpened] = useState(false);

  const allSelected = useMemo(
    () => selectedIds.length > 0 && filesList?.length === selectedIds.length,
    [filesList, selectedIds]
  );

  const isFileDeleteLoading = (fileId: string) => {
    return deleteFileIds.indexOf(fileId) !== -1;
  };

  const toggleAllChecked = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? filesList.map((item) => item.id) : []);
    },
    [filesList]
  );

  const selectItem = useCallback(
    (itemId: string, shiftKey: boolean = false) => {
      const currentIndex = filesList.findIndex((item) => item.id === itemId);

      // Shift 键多选逻辑
      if (shiftKey && lastSelectedIndex !== null && currentIndex !== -1) {
        const startIndex = Math.min(lastSelectedIndex, currentIndex);
        const endIndex = Math.max(lastSelectedIndex, currentIndex);
        const idsInRange = filesList.slice(startIndex, endIndex + 1).map((item) => item.id);

        setSelectedIds((prevIds) => {
          const newIds = new Set(prevIds);
          idsInRange.forEach((id) => newIds.add(id));
          return Array.from(newIds);
        });
        // 不更新 lastSelectedIndex，保持起始点不变
        return;
      }

      // 普通点击逻辑
      setSelectedIds((ids) => {
        if (ids.includes(itemId)) {
          setLastSelectedIndex(null);
          return ids.filter((id) => id !== itemId);
        }
        setLastSelectedIndex(currentIndex);
        return [...ids, itemId];
      });
    },
    [filesList, lastSelectedIndex]
  );

  const fileListPageOnchange = (page: number) => {
    if (page !== filesListPageInfo.page) {
      // 立即更新页码状态，让 UI 即时响应
      setFilesListPageInfo({ ...filesListPageInfoRef.current, page });
      isInitRef.current = true;
      getFileListInfo(page);
      setSelectedIds([]);
      setLastSelectedIndex(null);
    }
  };

  const getFileListInfo = async (page = 1, status?: ListFileStatus) => {
    if (!fileType) return;
    clearTimeout(listTimeoutRef.current);
    if (page !== filesListPageInfo.page) setFilesListPageInfo({ ...filesListPageInfoRef.current, page });
    isInitRef.current && setListLoading(true);
    cancelTokenSourceRef.current = axios.CancelToken.source();

    const fileStatus = status || statusValue || undefined;

    // 根据是否为website-pro路径使用不同的API
    const apiCall = isWebsitePro
      ? getWebConnectorFiles(datasetId, connectorId, {
          page: page,
          size: filesListPageInfo.size,
          query: initLoading ? search : debouncedSearch.normalize('NFD'),
          file_status: fileStatus,
          start_time: rangeDate[0],
          end_time: rangeDate[1],
          order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
        })
      : getDatasetsFilesList(
          {
            id: datasetId,
            file_type: fileType,
            page: page,
            size: filesListPageInfo.size,
            query: initLoading ? search : debouncedSearch.normalize('NFD'),
            file_status: fileStatus,
            source_type: subIntegrationSubType ? subIntegrationSubType : undefined,
            integration_rule_id: integrationRuleId,
            start_time: rangeDate[0],
            end_time: rangeDate[1],
            order_by: `${reverseSortDirection ? '-' : ''}${sortBy}`,
            learn_type_filter: learnTypeValue ? learnTypeValue : undefined,
          },
          cancelTokenSourceRef.current.token
        );

    await apiCall
      .then((res) => {
        const { items, pages, size, total } = res.data;

        // 根据数据源类型更新计数，setCount 方法会自动处理嵌套结构
        let countType: string;
        if (isWebsitePro) {
          countType = 'web-connector';
        } else if (fileType === 'integration') {
          // integration 类型需要根据子类型确定具体的计数字段
          // 目前只有 lark，后续可以根据 subIntegrationSubType 或路径进一步判断
          countType = 'lark';
        } else {
          countType = fileType;
        }
        setCount(countType as any, total);

        // 这里处理文件类型的title
        if (isFileType) {
          items.map((item) => {
            if (!item.title) {
              item.title = getFilenameWithoutExtension(item.filename);
            }
          });
        }

        setFilesList(items);
        setFilesListPageInfo({ page, total, pages, size });

        // check has process
        const processArr = items.filter(
          (item: DatasetsFilesInfo) => isFileStatusProcess(item.file_status) || isFileStatusReady(item.file_status)
        );
        if (processArr.length) {
          listTimeoutRef.current = setTimeout(() => {
            getFileListInfo(page);
          }, 5 * 1000);
        }
      })
      .finally(() => {
        setListLoading(false);
        setInitLoading(false);
        if (isInitRef.current) isInitRef.current = false;
      });
  };

  const refreshHistoryList = () => {
    const isCruuentPageLastItem = filesList.length === 1;
    const isLastPage = filesListPageInfo.page === filesListPageInfo.pages;

    const page =
      filesListPageInfo.page > 1 && (isCruuentPageLastItem || (allSelected && isLastPage))
        ? filesListPageInfo.page - 1
        : filesListPageInfo.page;

    getFileListInfo(page);
  };

  const deleteFile = async (fileId: string) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    setDeleteFileIds([...deleteFileIds, fileId]);
    await delDatasetsFiles({ id: datasetId, file_ids: [fileId] }).finally(() => {
      refreshHistoryList();
      setDeleteFileIds(deleteFileIds.filter((item) => item !== fileId));
    });
  };

  const handleDelete = () => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const deleteSelected = async () => {
      setDeleteFileIds([...deleteFileIds, ...selectedIds]);
      await delDatasetsFiles({ id: datasetId, file_ids: selectedIds }).then(() => {
        refreshHistoryList();
        setDeleteFileIds([]);
        setSelectedIds([]);
      });
    };
    openModal({
      title: t('faqs.delete.title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      children: <SelectedItemRemove deleteSelected={deleteSelected} />,
    });
  };

  const onEditTitle = (item: DatasetsFilesInfo) => {
    setEditModalOpened(true);
    setEditItem(item);
  };
  const onRetrain = (item: DatasetsFilesInfo) => {
    if (isFileType) {
      // 集成下面的文件类型的重新学习，需要调用新接口。
      if (fileType === 'integration') {
        refreshDatasetsIntegrationFile({
          datasetId,
          fileIds: [item.id],
          learn_type: 100, // onprem下，先将普通学习改成快速学习
          integration_rule_id: integrationRuleId,
        }).then(() => {
          getFileListInfo(filesListPageInfo.page);
        });
      } else {
        // onprem下，先将普通学习改成快速学习
        refreshDatasetsFile({ datasetId, fileIds: [item.id], learn_type: 100 }).then(() => {
          getFileListInfo(filesListPageInfo.page);
        });
      }
    } else {
      // 对于website和website-pro都使用website的刷新接口
      refreshDatasetsWebsite({ datasetId, fileIds: [item.id] }).then(() => {
        getFileListInfo(filesListPageInfo.page);
      });
    }
  };

  const handleRetrain = () => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    setRetrainLoading(true);
    if (isFileType) {
      if (fileType === 'integration') {
        refreshDatasetsIntegrationFile({
          datasetId,
          fileIds: selectedIds,
          learn_type: 100, // onprem下，先将普通学习改成快速学习
          integration_rule_id: integrationRuleId,
        }).then(() => {
          getFileListInfo(filesListPageInfo.page);
          setSelectedIds([]);
          setRetrainLoading(false);
        });
      } else {
        // onprem下，先将普通学习改成快速学习
        refreshDatasetsFile({ datasetId, fileIds: selectedIds, learn_type: 100 }).then(() => {
          getFileListInfo(filesListPageInfo.page);
          setSelectedIds([]);
          setRetrainLoading(false);
        });
      }
    } else {
      refreshDatasetsWebsite({ datasetId, fileIds: selectedIds }).then(() => {
        getFileListInfo(filesListPageInfo.page);
        setSelectedIds([]);
        setRetrainLoading(false);
      });
    }
  };

  const onAdvancedTrain = (item: DatasetsFilesInfo) => {
    handleAdvancedTrain(item.id);
  };

  const handleAdvancedTrain = (fileId?: string) => {
    openModal({
      title: (
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={2}>
            <Text>{t('embeddings.documents.advanced_train.title')}</Text>
            <Text size="sm" c="gray.5">
              {t('embeddings.documents.advanced_train.desc')}
            </Text>
          </Stack>
          <CloseButton className="flex-shrink-0" onClick={() => closeAllModals()} />
        </Group>
      ),
      withCloseButton: false,
      centered: true,
      children: (
        <AdvancedTrainModal
          datasetId={datasetId}
          integrationRuleId={isWebsitePro ? connectorId : integrationRuleId}
          filesList={filesList}
          fileIds={fileId ? [fileId] : selectedIds}
          onTrainSuccess={() => {
            closeAllModals();
            setSelectedIds([]);
            getFileListInfo(filesListPageInfo.page);
          }}
        />
      ),
    });
  };

  const resetData = () => {
    setSearch('');
    setStatusValue('');
    setLearnTypeValue('0');
    setRangeDate([]);
    setSortBy('');
    setReverseSortDirection(false);

    setCounter(0);
    setInitLoading(true);
    setFilesListPageInfo(InitPageInfo);

    isInitRef.current = true;
    cancelTokenSourceRef.current?.cancel('getFileListInfo: Operation canceled by the user.');
  };

  // 切换类型或知识库时，重置数据并清理轮询定时器
  useEffect(() => {
    resetData();
  }, [fileType, datasetId]);

  useEffect(() => {
    if (initLoading && fileType) {
      getFileListInfo();
    }
  }, [initLoading, fileType]);

  // ������Դ��
  useEffect(() => {
    if (counter) {
      isInitRef.current = true;
      getFileListInfo(filesListPageInfo.page);
    }
  }, [counter]);

  useEffect(() => {
    if (!isInitRef.current) {
      getFileListInfo();
    }
  }, [datasetId]);

  // ɸѡ����
  useEffect(() => {
    if (!isInitRef.current) {
      getFileListInfo();
      setSelectedIds([]);
      setLastSelectedIndex(null);
    }
  }, [debouncedSearch, statusValue, learnTypeValue, subIntegrationSubType, rangeDate, reverseSortDirection, sortBy]);

  useEffect(() => {
    if (queryStatus) {
      setStatusValue(queryStatus);
      getFileListInfo(1, queryStatus);
    }
  }, [queryStatus]);

  return (
    <>
      <section className="flex flex-col relative h-full">
        <section className="flex-none">
          <Group justify="space-between" mb={20} align="flex-start">
            <Group className="flex-1" gap={8}>
              <Input
                size="sm"
                className="grow xl:grow-0 xl:min-w-[30%]"
                leftSection={<SvgSearch />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder={t('embeddings.table.search_resource_title')}
              />
              {fileType === 'integration' && (
                <Select
                  size="sm"
                  style={{ width: 150 }}
                  value={subIntegrationSubType}
                  onChange={(value: string | null) => {
                    setSubIntegrationSubType(value as IntegrationSubType);
                  }}
                  data={[
                    { value: '', label: t('embeddings.table.filter_by_integration_sub_type.all') },
                    { value: 'lark', label: 'Lark' },
                  ]}
                  allowDeselect={false}
                />
              )}
              <Select
                size="sm"
                style={{ width: 120 }}
                value={statusValue}
                onChange={(value: string | null) => {
                  setStatusValue(value as ListFileStatus);
                }}
                data={[
                  { value: '', label: t('embeddings.table.filter_by_status.all') },
                  { value: 'complete', label: t('embeddings.table.filter_by_status.learned') },
                  { value: 'process', label: t('embeddings.table.filter_by_status.learning') },
                  { value: 'ready', label: t('embeddings.table.filter_by_status.ready') },
                  { value: 'fail', label: t('embeddings.table.filter_by_status.failed') },
                  { value: 'invalid', label: t('embeddings.table.filter_by_status.skipped') },
                ]}
                allowDeselect={false}
              />

              {isFileType && (
                <Select
                  size="sm"
                  style={{ width: 120 }}
                  value={learnTypeValue}
                  onChange={(value: string | null) => {
                    setLearnTypeValue(value as string);
                  }}
                  data={[
                    { value: '0', label: t('embeddings.table.filter_by_model.all') },
                    // 跟Cloud版本的learn_type保持一致。具体能查询出来什么数值，后端控制（比如 onprem 下后端会把markitdown当成普通学习，cloud 版本会把markdown当成高级学习）
                    { value: '1', label: t('embeddings.table.filter_by_model.normal') },
                    { value: '2', label: t('embeddings.table.filter_by_model.advanced') },
                  ]}
                  allowDeselect={false}
                />
              )}
              <DateSelector
                key={fileType}
                hasAll
                showCustomDateTime
                onDateChange={handleDateChange}
                radius={8}
                p={'7px 15px'}
                leftSection={<SvgCalenderGray />}
                styles={() => ({
                  root: {
                    color: '#000',
                    border: '1px solid #E4E7EC',
                    backgroundColor: '#FFF',

                    '&: hover': {
                      borderColor: '#5A4CB7',
                    },
                  },
                  leftIcon: {
                    color: 'gray',
                  },
                })}
              />

              {isFileType && (
                <>
                  <Button
                    size="sm"
                    // leftSection={<IconRefresh />}
                    disabled={selectedIds.length === 0}
                    onClick={handleRetrain}
                    loading={retrainLoading}
                  >
                    {/* {t('retrain')} */}
                    {/* onprem下，先将普通学习改成快速学习 */}
                    {t('embeddings.documents.quick_learning')}
                  </Button>
                  {supportAdvancedTrain && (
                    <CanAccess accessKey="enable_multi_modal_parsing">
                      <Button size="sm" disabled={selectedIds.length === 0} onClick={() => handleAdvancedTrain()}>
                        {t('embeddings.documents.advanced_train.button')}
                      </Button>
                    </CanAccess>
                  )}
                </>
              )}
            </Group>
            <section>
              <Button
                size="sm"
                color="red"
                leftSection={<SvgTrash />}
                variant="outline"
                className="filled"
                disabled={selectedIds.length === 0}
                onClick={handleDelete}
              >
                {t('faqs.action.delete')}
              </Button>
            </section>
          </Group>
        </section>
        <section className={`${!initLoading && 'flex-auto'} relative flex flex-col`}>
          {initLoading ? (
            <TableLoading />
          ) : (
            <>
              {!filesList.length && (
                <section className="h-[400px] flex items-center justify-center min-h-[200px] bg-white rounded-[20px] overflow-hidden border border-[#F8F6FF]">
                  {t('no_data')}
                </section>
              )}
              {!!filesList.length && (
                <>
                  <section className="flex-auto">
                    <section className="h-full rounded-xl overflow-hidden">
                      <Table className={classes.table} verticalSpacing="sm" highlightOnHover captionSide="bottom">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>
                              <Group wrap="nowrap">
                                <Checkbox checked={allSelected} onChange={() => toggleAllChecked(!allSelected)} />
                                <span>{t('embeddings.table.resource')}</span>
                              </Group>
                            </Table.Th>
                            <Table.Th>{t('embeddings.table.title')}</Table.Th>
                            {isFileType && (
                              <Table.Th style={{ whiteSpace: 'nowrap' }}>{t('embeddings.table.type')}</Table.Th>
                            )}
                            {isFileType ? (
                              <Th
                                sorted={sortBy === 'file_size'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('file_size')}
                              >
                                {t('embeddings.table.size')}
                              </Th>
                            ) : (
                              <Th
                                sorted={sortBy === 'characters_count'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('characters_count')}
                              >
                                {t('embeddings.table.characters')}
                              </Th>
                            )}
                            <Table.Th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                              {t('embeddings.table.status')}
                            </Table.Th>
                            {isFileType && supportAdvancedTrain && (
                              <Table.Th style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                {t('embeddings.table.model')}
                              </Table.Th>
                            )}
                            <Th
                              sorted={sortBy === 'token_count'}
                              reversed={reverseSortDirection}
                              onSort={() => setSorting('token_count')}
                            >
                              {t('embeddings.table.tokens')}
                            </Th>
                            <Th
                              sorted={sortBy === 'updated_at'}
                              reversed={reverseSortDirection}
                              onSort={() => setSorting('updated_at')}
                            >
                              {t('embeddings.table.last_modified')}
                            </Th>
                            <Table.Th style={{ whiteSpace: 'nowrap' }}>{t('embeddings.table.action')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>

                        <Table.Tbody>
                          {listLoading ? (
                            <LoadingOverlay className={'min-h-[200px]'} visible={listLoading} />
                          ) : (
                            <>
                              {filesList.map((item) => {
                                return (
                                  <TableItem
                                    key={item.id}
                                    type={fileType}
                                    isFileType={isFileType}
                                    item={item}
                                    selected={selectedIds.includes(item.id)}
                                    onSelectedChange={(shiftKey) => selectItem(item.id, shiftKey)}
                                    deleteFile={deleteFile}
                                    isFileDeleteLoading={isFileDeleteLoading}
                                    onEditTitle={() => onEditTitle(item)}
                                    onRetrain={() => onRetrain(item)}
                                    onAdvancedTrain={() => onAdvancedTrain(item)}
                                  />
                                );
                              })}
                            </>
                          )}
                        </Table.Tbody>
                      </Table>
                    </section>
                  </section>
                  <section className="flex-none">
                    {filesListPageInfo.pages > 1 && (
                      <Group className="px-2 sm:px-8 mt-3 mb-4" justify="center">
                        <Pagination
                          value={filesListPageInfo.page}
                          total={filesListPageInfo.pages}
                          onChange={fileListPageOnchange}
                        />
                      </Group>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </section>
      </section>

      <Modal opened={editModalOpened} onClose={() => setEditModalOpened(false)} title={t('edit')} centered>
        {editItem && (
          <EditForm
            fileId={editItem.id}
            title={editItem.title || ''}
            onSubmitCallback={() => {
              setEditModalOpened(false);
              refreshHistoryList();
            }}
          />
        )}
      </Modal>
    </>
  );
};
